import { ELightGalleryType } from '@quesmed/types/models';
import { z } from 'zod';

const pictureSchema = z.object({
  id: z.number().or(z.string()).optional().nullable(),
  name: z.string(),
  caption: z.string().optional().nullable(),
  sensitive: z.boolean(),
  path: z.string(),
  path512: z.string(),
  path256: z.string(),
  thumbhash: z.string().optional().nullable(),
  overlayPath: z.string().optional().nullable(),
  overlayPath256: z.string().optional().nullable(),
  overlayPath512: z.string().optional().nullable(),
});
const assetSchema = z.object({
  id: z.string(),
  index: z.number().nullable(),
  choiceLabel: z.string().optional().nullable(),
  recordId: z.string().optional().nullable(),
  jobId: z.string(),
  createdAt: z.date().or(z.number()),
  linked: z.boolean().optional().nullable(),
  deleted: z.boolean().optional().nullable(),
  type: z.enum(ELightGalleryType, { message: 'Invalid enum value' }),
  picture: pictureSchema,
});

export const assetFieldMapping: Record<ELightGalleryType, string> = {
  [ELightGalleryType.QUESTION]: 'question',
  [ELightGalleryType.EXPLANATION]: 'explanation',
  [ELightGalleryType.LEARNING_POINT]: 'learningPoint',
  [ELightGalleryType.STEM]: 'stem',
  [ELightGalleryType.CHOICE]: 'choices',
  [ELightGalleryType.CHOICE_EXPLANATION]: 'choices',
  [ELightGalleryType.ACTOR]: 'actorBrief',
  [ELightGalleryType.EXAMINER]: 'examinerBrief',
  [ELightGalleryType.CANDIDATE]: 'candidateBrief',
  [ELightGalleryType.WALKTHROUGH]: 'walkthroughBrief',
  [ELightGalleryType.CLINICAL_DATA]: 'clinicalData',
};

export type IValidateAsset = {
  field?: string | null;
  fieldName: string;
  path: (string | number)[];
  assets: NonNullable<z.infer<typeof assetSchema>[]>;
};

const galleryRegex = /\[lightgallery\d*\]/g;
// const galleryRegex = /\[?\s*(?:l\s*i\s*g\s*h\s*t)?\s*(?:g\s*a\s*l\s*l\s*e\s*r\s*y)?\d*(?:-[^\]\s]+)?\s*\]?/gi;

const validateFieldWithAssets = ({ field, fieldName, path, assets }: IValidateAsset) => {
  if (!field) return [];

  const tagsInFieldArr = field.match(galleryRegex) ?? [];
  const tagsInField = new Set(tagsInFieldArr);
  const issues = [];

  // Forward check: every asset must exist in field
  for (const asset of assets) {
    const tag = `[lightgallery${asset.index || ''}]`;
    if (!tagsInField.has(tag)) {
      issues.push({
        code: 'custom',
        path,
        message: `${fieldName} must contain ${tag}`,
      });
    }
  }

  // Reverse check: every tag must have a matching asset
  for (const tag of tagsInField) {
    const idx = tag.match(/\d+/)?.[0] ?? '0';
    const hasAsset = assets.some((a) => String(a.index ?? '0') === idx);
    if (!hasAsset) {
      issues.push({
        code: 'custom',
        path,
        message: `${fieldName} must not contain ${tag} without an asset`,
      });
    }
  }

  return issues;
};

export type IAssetSchema = z.infer<typeof assetSchema>;

export { assetSchema, pictureSchema, validateFieldWithAssets };
